import { ArrowUpRightIcon } from '@phosphor-icons/react';

import {
  BRAND_KIT_MENU_VALUES,
  BRAND_KIT_ROW_LABEL,
  KitSwatch,
  kitIdToMenuValue,
  menuValueToKitId,
} from './brand-kit-menu';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface BrandKitPickerKit {
  id: string;
  name: string;
  is_default: boolean;
  primary_color?: string | null;
  secondary_color?: string | null;
}

interface BrandKitPickerProps {
  /**
   * Currently-active kit id, or null to mean "use default".
   */
  value: string | null;
  /**
   * Fired with kit-id, null (= use default), or 'off' (= no kit applied).
   */
  onChange: (kitId: string | null, options?: { off?: boolean }) => void;
  kits: BrandKitPickerKit[];
  offAllowed?: boolean;
  compact?: boolean;
  className?: string;
  placeholder?: string;
  onEditClick?: () => void;
  /** Hide the virtual “use my default” row when choosing a concrete brand. */
  showDefaultOption?: boolean;
}

const ROW_LABEL = BRAND_KIT_ROW_LABEL;

export function BrandKitPicker({
  value,
  onChange,
  kits,
  offAllowed = false,
  compact = false,
  className,
  placeholder = 'Select a brand kit',
  onEditClick,
  showDefaultOption = true,
}: BrandKitPickerProps) {
  const single = kits.length === 1 ? kits[0] : null;
  const selectValue = kitIdToMenuValue(value);

  const handleChange = (raw: string) => {
    const parsed = menuValueToKitId(raw);
    if (!parsed) return;
    onChange(parsed.kitId, parsed.off ? { off: true } : undefined);
  };

  if (kits.length === 0) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onEditClick?.();
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-caption text-muted-foreground hover:bg-muted/40 hover:text-foreground',
          className,
        )}
      >
        <span className={ROW_LABEL}>Brand kit</span>
        <span className="flex items-center gap-1 text-detail text-muted-foreground/70">
          <span>Add kit</span>
          <ArrowUpRightIcon className="size-3" weight="regular" />
        </span>
      </button>
    );
  }

  return (
    <Select value={selectValue} onValueChange={handleChange}>
      <SelectTrigger
        className={cn(
          // `dark:bg-transparent` pairs the override so it survives
          // tailwind-merge alongside `SelectTrigger`'s own `dark:bg-input` —
          // a bare `bg-transparent` and a `dark:`-prefixed utility are
          // different merge groups and would otherwise fight over dark-mode
          // background-color by stylesheet source order.
          'h-11 w-full border-0 bg-transparent dark:bg-transparent px-2 py-1.5 text-caption hover:bg-muted/40 focus:ring-0 focus:ring-offset-0 data-[state=open]:bg-muted/40 [&>svg]:opacity-60 md:h-8',
          className,
        )}
        aria-label="Active brand kit"
      >
        <SelectValue placeholder={placeholder}>
          {compact ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <span className={ROW_LABEL}>Brand kit</span>
              <span className="text-detail text-muted-foreground/70 truncate">
                {value
                  ? (kits.find((k) => k.id === value)?.name ?? 'Brand kit')
                  : single
                    ? single.name
                    : 'Default'}
              </span>
            </span>
          ) : (
            <span className="block truncate text-caption text-foreground">
              {value
                ? (kits.find((k) => k.id === value)?.name ?? 'Brand kit')
                : 'Use my default kit'}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)]">
        {kits.length > 1 && (
          <SelectGroup>
            <SelectLabel className={cn('px-2 py-1 pl-2', SECTION_LABEL)}>Your kits</SelectLabel>
            {showDefaultOption ? (
              <SelectItem value={BRAND_KIT_MENU_VALUES.DEFAULT}>
                <span className="text-caption text-foreground">Use my default kit</span>
              </SelectItem>
            ) : null}
            {kits.map((kit) => (
              <SelectItem
                key={kit.id}
                value={kitIdToMenuValue(kit.id)}
                title={kit.name}
                className="min-w-0 overflow-hidden py-3 md:py-1.5"
              >
                <span className="flex w-full min-w-0 items-center gap-2 overflow-hidden">
                  <KitSwatch color={kit.primary_color} />
                  <span className="min-w-0 flex-1 truncate text-caption text-foreground">
                    {kit.name}
                    {kit.is_default ? (
                      <span className="ml-1 text-detail text-muted-foreground/70">· default</span>
                    ) : null}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {kits.length === 1 && (
          <SelectGroup>
            <SelectItem
              value={
                showDefaultOption && value === null
                  ? BRAND_KIT_MENU_VALUES.DEFAULT
                  : kitIdToMenuValue(single!.id)
              }
            >
              <span className="text-caption text-foreground">{single?.name ?? 'Brand kit'}</span>
            </SelectItem>
          </SelectGroup>
        )}
        {offAllowed && (
          <SelectGroup>
            <SelectLabel className={cn('px-2 py-1 pl-2', SECTION_LABEL)}>Off</SelectLabel>
            <SelectItem value={BRAND_KIT_MENU_VALUES.OFF}>
              <span className="text-caption text-foreground">No kit (off)</span>
            </SelectItem>
          </SelectGroup>
        )}
        {onEditClick && (
          <>
            <div className="my-1 h-px bg-border/40" />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEditClick();
              }}
              className="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-muted/40 transition-colors"
            >
              <span className={ROW_LABEL}>Edit brand kit</span>
              <ArrowUpRightIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
            </button>
          </>
        )}
      </SelectContent>
    </Select>
  );
}

export const BRAND_KIT_PICKER_VALUES = BRAND_KIT_MENU_VALUES;
